import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Divider
} from '@mui/material';
import {
  WhatsApp,
  QrCode2,
  CheckCircle,
  Error,
  Send,
  PowerSettingsNew,
  Refresh,
  History
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Criar instância do axios com token
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  }
});

export default function WhatsAppBaileys() {
  // Estados
  const [carregando, setCarregando] = useState(false);
  const [status, setStatus] = useState({
    conectado: false,
    status: 'desconectado',
    numero: null,
    nome: null,
    qrcode: null,
    ultimaConexao: null
  });
  
  // Modal QR Code
  const [modalQR, setModalQR] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [tempoRestante, setTempoRestante] = useState(120);
  
  // Modal Enviar Mensagem
  const [modalEnviar, setModalEnviar] = useState(false);
  const [numeroDestino, setNumeroDestino] = useState('');
  const [mensagemTexto, setMensagemTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  
  // Mensagens
  const [alerta, setAlerta] = useState({ tipo: '', mensagem: '' });

  // Carregar status ao montar
  useEffect(() => {
    carregarStatus();
    
    // Atualizar status a cada 5 segundos se não estiver conectado
    const interval = setInterval(() => {
      if (!status.conectado) {
        carregarStatus();
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  // Timer do QR Code
  useEffect(() => {
    let timer;
    if (modalQR && tempoRestante > 0) {
      timer = setInterval(() => {
        setTempoRestante(prev => {
          if (prev <= 1) {
            setAlerta({ tipo: 'warning', mensagem: 'QR Code expirou. Gerando novo...' });
            setTimeout(() => conectarWhatsApp(), 2000);
            return 120;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [modalQR, tempoRestante]);

  // Verificar conexão enquanto QR está aberto
  useEffect(() => {
    let interval;
    if (modalQR) {
      interval = setInterval(async () => {
        const statusAtual = await carregarStatus();
        if (statusAtual.conectado) {
          setModalQR(false);
          setAlerta({ tipo: 'success', mensagem: '✅ WhatsApp conectado com sucesso!' });
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [modalQR]);

  /**
   * Carregar status da conexão
   */
  const carregarStatus = async () => {
    try {
      const response = await api.get('/whatsapp/status');
      
      if (response.data.sucesso) {
        setStatus(response.data.dados);
        return response.data.dados;
      }
    } catch (erro) {
      console.error('Erro ao carregar status:', erro);
    }
  };

  /**
   * Conectar WhatsApp e gerar QR Code
   */
  const conectarWhatsApp = async () => {
    try {
      setCarregando(true);
      setAlerta({ tipo: '', mensagem: '' });
      
      const response = await api.post('/whatsapp/conectar');
      
      if (response.data.sucesso) {
        if (response.data.qrcode) {
          // QR Code gerado
          setQrCodeData(response.data.qrcode);
          setModalQR(true);
          setTempoRestante(120);
          setAlerta({ tipo: 'info', mensagem: 'QR Code gerado! Escaneie com seu WhatsApp.' });
        } else if (response.data.dados?.conectado) {
          // Já está conectado
          setStatus(response.data.dados);
          setAlerta({ tipo: 'success', mensagem: '✅ WhatsApp já está conectado!' });
        } else {
          // Aguardando
          setAlerta({ tipo: 'warning', mensagem: response.data.mensagem || 'Aguarde...' });
          setTimeout(() => carregarStatus(), 3000);
        }
      } else {
        setAlerta({ tipo: 'error', mensagem: response.data.mensagem || 'Erro ao conectar' });
      }
      
    } catch (erro) {
      console.error('Erro ao conectar:', erro);
      setAlerta({ tipo: 'error', mensagem: erro.response?.data?.erro || 'Erro ao conectar WhatsApp' });
    } finally {
      setCarregando(false);
    }
  };

  /**
   * Desconectar WhatsApp
   */
  const desconectarWhatsApp = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) {
      return;
    }
    
    try {
      setCarregando(true);
      const response = await api.post('/whatsapp/desconectar');
      
      if (response.data.sucesso) {
        setStatus({
          conectado: false,
          status: 'desconectado',
          numero: null,
          nome: null,
          qrcode: null
        });
        setAlerta({ tipo: 'success', mensagem: '✅ WhatsApp desconectado com sucesso' });
      } else {
        setAlerta({ tipo: 'error', mensagem: response.data.erro || 'Erro ao desconectar' });
      }
      
    } catch (erro) {
      console.error('Erro ao desconectar:', erro);
      setAlerta({ tipo: 'error', mensagem: erro.response?.data?.erro || 'Erro ao desconectar' });
    } finally {
      setCarregando(false);
    }
  };

  /**
   * Enviar mensagem de teste
   */
  const enviarMensagem = async () => {
    try {
      setEnviando(true);
      
      if (!numeroDestino || !mensagemTexto) {
        setAlerta({ tipo: 'warning', mensagem: 'Preencha número e mensagem' });
        return;
      }
      
      const response = await api.post('/whatsapp/enviar', {
        numero: numeroDestino,
        mensagem: mensagemTexto
      });
      
      if (response.data.sucesso) {
        setAlerta({ tipo: 'success', mensagem: '✅ Mensagem enviada com sucesso!' });
        setModalEnviar(false);
        setNumeroDestino('');
        setMensagemTexto('');
      } else {
        setAlerta({ tipo: 'error', mensagem: response.data.erro || 'Erro ao enviar' });
      }
      
    } catch (erro) {
      console.error('Erro ao enviar mensagem:', erro);
      setAlerta({ tipo: 'error', mensagem: erro.response?.data?.erro || 'Erro ao enviar mensagem' });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <WhatsApp sx={{ fontSize: 40, color: '#25D366', mr: 2 }} />
        <Typography variant="h4" component="h1">
          WhatsApp - Baileys Nativo
        </Typography>
      </Box>

      {/* Alertas */}
      {alerta.mensagem && (
        <Alert 
          severity={alerta.tipo} 
          onClose={() => setAlerta({ tipo: '', mensagem: '' })}
          sx={{ mb: 3 }}
        >
          {alerta.mensagem}
        </Alert>
      )}

      {/* Card de Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Status da Conexão
            </Typography>
            <IconButton onClick={carregarStatus} disabled={carregando}>
              <Refresh />
            </IconButton>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {/* Status */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {status.conectado ? (
                  <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                ) : (
                  <Error sx={{ color: 'error.main', mr: 1 }} />
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={status.status.toUpperCase()} 
                    color={status.conectado ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Box>
            </Grid>

            {/* Número Conectado */}
            {status.numero && (
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Número Conectado
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {status.numero}
                  </Typography>
                  {status.nome && (
                    <Typography variant="body2" color="text.secondary">
                      {status.nome}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}

            {/* Última Conexão */}
            {status.ultimaConexao && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Última conexão: {new Date(status.ultimaConexao).toLocaleString('pt-BR')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Botões de Ação */}
      <Paper sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {!status.conectado ? (
            <Grid item xs={12} md={6}>
              <Button
                fullWidth
                variant="contained"
                color="success"
                size="large"
                startIcon={<QrCode2 />}
                onClick={conectarWhatsApp}
                disabled={carregando}
              >
                {carregando ? <CircularProgress size={24} /> : 'Conectar WhatsApp'}
              </Button>
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<Send />}
                  onClick={() => setModalEnviar(true)}
                >
                  Enviar Mensagem Teste
                </Button>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={carregarStatus}
                >
                  Atualizar Status
                </Button>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  color="error"
                  startIcon={<PowerSettingsNew />}
                  onClick={desconectarWhatsApp}
                  disabled={carregando}
                >
                  Desconectar
                </Button>
              </Grid>
            </>
          )}
        </Grid>

        {/* Informações */}
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>ℹ️ Importante:</strong> Apenas 1 número pode estar conectado por vez. 
              Para conectar outro número, desconecte o atual primeiro.
            </Typography>
          </Alert>
        </Box>
      </Paper>

      {/* Modal QR Code */}
      <Dialog open={modalQR} onClose={() => setModalQR(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <QrCode2 sx={{ mr: 1, color: '#25D366' }} />
            Conectar WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {/* QR Code */}
            {qrCodeData && (
              <img 
                src={qrCodeData} 
                alt="QR Code WhatsApp" 
                style={{ width: '100%', maxWidth: 400, borderRadius: 8 }}
              />
            )}

            {/* Timer */}
            <Box sx={{ mt: 2 }}>
              <Chip 
                label={`Expira em: ${Math.floor(tempoRestante / 60)}:${(tempoRestante % 60).toString().padStart(2, '0')}`}
                color="warning"
                icon={<History />}
              />
            </Box>

            {/* Instruções */}
            <Box sx={{ mt: 3, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>
                📱 Como conectar:
              </Typography>
              <Typography variant="body2" component="ol" sx={{ pl: 2 }}>
                <li>Abra o WhatsApp no seu celular</li>
                <li>Toque em <strong>Menu</strong> → <strong>Dispositivos Conectados</strong></li>
                <li>Toque em <strong>Conectar Dispositivo</strong></li>
                <li>Aponte a câmera para este QR Code</li>
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalQR(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal Enviar Mensagem */}
      <Dialog open={modalEnviar} onClose={() => setModalEnviar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Send sx={{ mr: 1, color: 'primary.main' }} />
            Enviar Mensagem de Teste
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Número de Destino"
              placeholder="5592999999999"
              value={numeroDestino}
              onChange={(e) => setNumeroDestino(e.target.value)}
              helperText="Digite apenas números com DDD e DDI (ex: 5592999999999)"
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Mensagem"
              multiline
              rows={4}
              placeholder="Digite sua mensagem de teste..."
              value={mensagemTexto}
              onChange={(e) => setMensagemTexto(e.target.value)}
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Esta mensagem será enviada do número conectado para o número de destino.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalEnviar(false)}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={enviarMensagem}
            disabled={enviando || !numeroDestino || !mensagemTexto}
            startIcon={enviando ? <CircularProgress size={20} /> : <Send />}
          >
            {enviando ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
